import dynamic from "next/dynamic";

import type { Insight } from "models/insight";

import useAuthentication from "hooks/useAuthentication";
import { corporatePlan } from "consts/urls";

const AccountUpgradeForm = dynamic(
  () => import("components/AccountUpgradeForm")
);

const DiscussionRestrictionState = ({
  insight,
  isViewing = true,
  textClassName,
}: {
  insight?: Insight;
  isViewing?: boolean;
  textClassName?: string;
}) => {
  const { account: loggedInAccount } = useAuthentication();

  const defaultMessage = (
    <p>You cannot {isViewing ? "view" : "post"} discussions here.</p>
  );

  const upgradeLink = (
    <>
      <a
        href="#account-upgrade-form"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          document
            .getElementById("account-upgrade-form")
            ?.scrollIntoView({ behavior: "smooth" });
        }}
        className="text-smart-teal"
      >
        {loggedInAccount?.plan ? "Upgrade your plan" : "Subscribe to a plan"}
      </a>{" "}
      now.
    </>
  );

  if (insight) {
    if (!insight.isPublished) {
      return <p>No discussions found.</p>;
    }
    if (insight.isEmbargoed) {
      return (
        <p>Sorry, you can only view discussions for available Insights.</p>
      );
    }
    if (insight.viewerIsRestricted && loggedInAccount?.canViewComment) {
      return defaultMessage;
    }
  }

  if (loggedInAccount?.canViewComment) return null;

  const reason = loggedInAccount?.cannotViewCommentReason;

  if (reason === "company-disable-viewing-comments") {
    return (
      <p>Your Compliance Team has restricted your access to this feature.</p>
    );
  }

  if (reason === "preview-pass") {
    return (
      <>
        <p className={textClassName}>
          Sorry, you cannot view discussions as a {loggedInAccount?.accountType}{" "}
          account. {upgradeLink}
        </p>
        {!insight && <AccountUpgradeForm />}
      </>
    );
  }

  if (reason === "individual-pass") {
    return (
      <p className={textClassName}>
        Sorry, you cannot view discussions as a {loggedInAccount?.accountType}{" "}
        subscriber.
      </p>
    );
  }

  if (reason === "insta-pass") {
    return (
      <>
        <p className={textClassName}>
          Sorry, you cannot post discussions under {loggedInAccount?.plan?.name}{" "}
          plan. {upgradeLink}
        </p>
        {!insight && <AccountUpgradeForm />}
      </>
    );
  }

  if (reason === "company-disable-posting-comments") {
    return (
      <p className={textClassName}>
        Sorry, your company has disabled posting discussions.
      </p>
    );
  }

  if (reason === "corporate-officer-disable-viewing-comments") {
    return (
      <>
        <p className={textClassName}>
          Sorry, you cannot view discussions under{" "}
          {loggedInAccount?.company?.corporatePlan?.name} Plan.{" "}
          <a href={corporatePlan} target="_blank" className="underline">
            Upgrade your plan
          </a>{" "}
          now to read Discussions.
        </p>
      </>
    );
  }

  if (reason === "syndicator-ip") {
    return (
      <p className={textClassName}>
        Sorry, you cannot view discussions as a Syndicator.
      </p>
    );
  }

  if (reason === "issuer-paid-ip") {
    return (
      <p className={textClassName}>
        Sorry, you cannot view discussions as a Issuer-paid Insight Provider.
      </p>
    );
  }

  return defaultMessage;
};

export default DiscussionRestrictionState;
